/**
 * Wrap the conditions an entry declares where they appear in its own prose
 * (issue 36, phase 4b) — so a spell that says "shaken" links to the Shaken
 * condition without a reader leaving the page to find out what it means.
 *
 * THE DOUBLE GATE, AND WHY IT COLLAPSES TO ONE CHECK HERE.
 * A word is annotated only if (a) it is one of the Tier A terms unambiguous in
 * D&D prose, and (b) the entry actually declares that condition. Phase 4a only
 * ever links Tier A terms, so an entry's `conditions` reference is ALREADY
 * Tier-A-filtered — which means this function needs no term list of its own and
 * cannot drift from the one the data was built with. Feed it the entry's own
 * conditions and both halves of the gate are satisfied by construction.
 *
 * Without both halves this would be actively wrong rather than merely
 * incomplete: `prone`, `dead`, `turned` and `stable` are ordinary English, and
 * a reader told that a spell inflicts a condition it does not is worse off than
 * a reader given no link at all.
 *
 * ⚠️ RUNS ON SANITISED HTML, NEVER ON AUTHORED HTML. Authored content is
 * untrusted (see utils/sanitizeHtml.js); this is a transform on what the
 * sanitiser already passed, and it inserts only anchors it builds itself from
 * a slug that has been escaped. Text inside an existing link, a heading, or a
 * code block is left alone so links cannot nest and headings keep their shape.
 */

const SKIP_TAGS = new Set(['a', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'code', 'pre']);

const escapeHtml = (s) => String(s)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

const escapeRegExp = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * "Flat-Footed" also appears as "flat footed"; match either.
 *
 * The leading boundary is a capture group rather than a lookbehind on purpose:
 * lookbehind is ES2018 and throws a SyntaxError when the regex is CONSTRUCTED
 * on older Safari, which would take down the whole detail page rather than
 * degrade this one feature.
 */
function termPattern(name) {
  const body = escapeRegExp(String(name).trim()).replace(/\\?-/g, '[- ]');
  return new RegExp(`(^|[^\\w-])(${body})(?![\\w-])`, 'i');
}

/**
 * @param {string} html      sanitised HTML
 * @param {Array}  conditions [{ slug, name }] — the entry's declared conditions
 * @returns {string} html with the first occurrence of each condition wrapped
 */
export default function annotateConditions(html, conditions) {
  if (!html || !Array.isArray(conditions) || !conditions.length) return html || '';

  const terms = conditions
    .filter((c) => c && c.slug && c.name)
    .map((c) => ({ slug: c.slug, name: c.name, re: termPattern(c.name) }));
  if (!terms.length) return html;

  // Only the FIRST mention of each condition is linked. Wrapping every
  // occurrence turns a paragraph that says "shaken" four times into four
  // identical links, which is noise rather than navigation.
  const used = new Set();
  const skipDepth = [];
  const out = [];

  // Split into tags and text runs. The input is sanitised, so tags are
  // well-formed and this needs no HTML parser.
  const parts = String(html).split(/(<[^>]+>)/);

  for (const part of parts) {
    if (!part) continue;
    if (part[0] === '<') {
      const close = part[1] === '/';
      const tag = (part.match(/^<\/?\s*([a-zA-Z][a-zA-Z0-9]*)/) || [])[1];
      const name = tag ? tag.toLowerCase() : '';
      if (name && SKIP_TAGS.has(name) && !/\/>$/.test(part)) {
        if (close) skipDepth.pop();
        else skipDepth.push(name);
      }
      out.push(part);
      continue;
    }

    if (skipDepth.length) { out.push(part); continue; }

    // Consume the text run left to right, taking the EARLIEST unused term each
    // time. Rewriting the whole run per term would re-scan the anchors already
    // inserted, so a later term could match inside an earlier one's href.
    let rest = part;
    while (rest) {
      let best = null;
      for (const t of terms) {
        if (used.has(t.slug)) continue;
        const m = t.re.exec(rest);
        if (!m) continue;
        const at = m.index + m[1].length;   // skip the boundary character
        if (!best || at < best.at) best = { t, at, word: m[2] };
      }
      if (!best) { out.push(rest); break; }

      used.add(best.t.slug);
      out.push(rest.slice(0, best.at));
      out.push(
        `<a class="condition-term" data-condition="${escapeHtml(best.t.slug)}"`
        + ` href="/conditions?slug=${encodeURIComponent(best.t.slug)}"`
        + ` target="_blank" rel="noopener">${escapeHtml(best.word)}</a>`
      );
      rest = rest.slice(best.at + best.word.length);
    }
  }

  return out.join('');
}
